import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './Docs.css';

export default function PresenceOnlineOffline() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link
          rel="canonical"
          href="https://www.joegalaxy.net/docs/presence-online-offline"
        />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Online vs Offline Presence: A Lifecycle-Aware Model</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          Online and offline look binary, but <strong>real user sessions are noisy</strong>.
          <br />
          Tabs suspend, mobile radios sleep, and networks partition without clean disconnects.
        </p>

        <p>
          This document explains why binary presence fails and how a{' '}
          <strong>lifecycle-aware, signal-based model</strong> produces presence you can trust
          without flapping or false offline states.
        </p>

        <hr />

        <section>
          <h2>Why binary online/offline presence fails</h2>
          <p>
            A socket <code>connect</code> event does not mean a human is active.
            <br />
            A <code>disconnect</code> event does not mean they are gone.
          </p>
          <p>Real systems experience:</p>
          <ul>
            <li>background tab suspension</li>
            <li>mobile OS network freezes</li>
            <li>transient Wi-Fi handoffs</li>
            <li>reconnects without explicit closes</li>
          </ul>
          <p>If presence is stored as a user-level boolean:</p>
          <pre>
            <code>{`user.isOnline = true;`}</code>
          </pre>
          <p>This approach fails immediately.</p>
          <p>A single user can:</p>
          <ul>
            <li>open multiple tabs</li>
            <li>use multiple devices</li>
            <li>background a session</li>
            <li>lose network connectivity transiently</li>
          </ul>
          <p>If you toggle a user-level boolean on connect and disconnect, your API will:</p>
          <ul>
            <li>undercount active users</li>
            <li>overcount availability</li>
            <li>flap between states</li>
            <li>mislead downstream systems</li>
          </ul>
          <p>
            <strong>Online is not a property of a user.</strong>
            <br />
            It is an <strong>emergent property of active sessions over time</strong>.
          </p>
        </section>

        <hr />

        <section>
          <h2>Presence as a signal</h2>
          <p>
            Presence should be modeled as a <strong>time-varying signal</strong>, not a stored
            flag.
          </p>
          <p>A signal-based model:</p>
          <ul>
            <li>decays without refresh</li>
            <li>expresses confidence instead of certainty</li>
            <li>separates transport connectivity from human availability</li>
          </ul>
          <p>
            This allows systems to coordinate behavior without mistaking network state for intent.
          </p>
          <p>
            <strong>Presence is a signal, not a status.</strong>
          </p>
        </section>

        <hr />

        <section>
          <h2>Lifecycle-aware presence states</h2>
          <p>
            Lifecycle-aware presence uses explicit states that map to system behavior rather than
            UI labels.
          </p>
          <p>Typical states include:</p>
          <ul>
            <li>
              <strong>active</strong> — foreground activity or recent interaction
            </li>
            <li>
              <strong>idle</strong> — connected but not interacting
            </li>
            <li>
              <strong>away</strong> — backgrounded or long idle
            </li>
            <li>
              <strong>stale</strong> — heartbeat missed but within a grace window
            </li>
            <li>
              <strong>offline</strong> — expired session or explicit leave
            </li>
          </ul>
          <p>
            State is derived from <strong>time since last signal</strong> and client lifecycle
            events, not written directly on the user.
          </p>
        </section>

        <hr />

        <section>
          <h2>Session-first architecture</h2>
          <p>
            Presence must be <strong>session-first</strong>, not user-first.
          </p>
          <p>Each browser tab, device, or SDK instance maintains its own session with:</p>
          <ul>
            <li>independent heartbeats</li>
            <li>independent lifecycle</li>
            <li>independent expiration</li>
          </ul>
          <p>User-level presence is <strong>derived</strong>, not stored.</p>
          <p>This avoids:</p>
          <ul>
            <li>overwriting state from multiple devices</li>
            <li>false offline transitions</li>
            <li>brittle reconnect logic</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>Architecture and timing</h2>
          <p>A reliable presence system includes:</p>
          <ul>
            <li>server-authoritative time</li>
            <li>TTL-based session storage</li>
            <li>grace windows for jitter</li>
            <li>adaptive heartbeat intervals</li>
          </ul>
          <p>
            Offline is a <strong>derived state</strong> when TTL expires, not a reaction to a single
            disconnect event.
          </p>
          <p>
            Short heartbeats are used for active sessions.
            <br />
            Longer intervals apply to idle or background states.
          </p>
        </section>

        <hr />

        <section>
          <h2>Aggregation and display</h2>
          <p>
            If a user-level indicator is required, derive it from the most recent session state.
          </p>
          <p>A simple precedence rule works well:</p>
          <ul>
            <li>
              <strong>active</strong> beats idle
            </li>
            <li>
              <strong>idle</strong> beats away
            </li>
            <li>
              <strong>away</strong> beats stale
            </li>
            <li>
              <strong>stale</strong> beats offline
            </li>
          </ul>
          <p>Expose time context when possible.</p>
          <p>Displaying “active 30s ago” is more truthful than a brittle online badge.</p>
        </section>

        <hr />

        <section>
          <h2>Common pitfalls</h2>
          <ul>
            <li>Treating authentication as presence</li>
            <li>Storing user-level online flags</li>
            <li>Expiring sessions immediately on disconnect</li>
            <li>Ignoring background tab suspension</li>
            <li>Broadcasting every heartbeat instead of reducing to state changes</li>
            <li>Using slow databases instead of TTL-based stores</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>Minimal pattern (conceptual)</h2>
          <pre>
            <code>{`// Server-side: record presence signal
onHeartbeat(sessionId, clientState, now) {
  store.set(
    sessionId,
    { state: clientState, lastSeenAt: now },
    TTL_MS
  );
}

// Derived state on read
function deriveState(lastSeenAt, now) {
  const age = now - lastSeenAt;

  if (age < 15_000) return 'active';
  if (age < 60_000) return 'idle';
  if (age < 180_000) return 'away';
  if (age < 240_000) return 'stale';

  return 'offline';
}`}</code>
          </pre>
          <p>
            Thresholds vary by product.
            <br />
            The pattern does not.
          </p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>Binary online/offline presence collapses under real-world conditions.</p>
          <p>
            A lifecycle-aware, session-first, TTL-derived model produces presence that is:
          </p>
          <ul>
            <li>stable</li>
            <li>truthful</li>
            <li>coordination-ready</li>
          </ul>
          <p>
            This model scales cleanly from simple activity indicators to presence-native A/V and
            real-time collaboration.
          </p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
            </li>
            <li>
              <Link to="/docs/webrtc-presence.md">WebRTC Presence</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/presence-online-offline.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
